'use client';

import { cn, useDisclosure } from '@heroui/react';
import { useMediaQuery } from 'usehooks-ts';

import { useFeatureFlag } from '@/@feature-flags';
import { NavShell } from '@/@nav/components/nav-shell';
import { shouldShowViewAsControl } from '@/@subsidiary/visibility';

import { studioService } from '@/@studio/services';
import { Routes } from '@/app/routes';
import { SubmitMessageModal } from '@/components/ui/organisms/SubmitMessageModal';
import { ISSUE_AREA_OPTIONS } from '@/lib/utils/constants';
import { useSession } from '@/providers/enhanced-session';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useCallback, useState, type Key } from 'react';
import SidebarDrawer from './SidebarDrawer';

const ViewAsSidebarControl = dynamic(
    () =>
        import('@/@subsidiary/components/ViewAsSidebarControl').then((mod) => ({
            default: mod.ViewAsSidebarControl,
        })),
    { ssr: false, loading: () => null },
);

export default function WithSidebar() {
    const { data: session } = useSession();
    const { isOpen, onOpenChange } = useDisclosure();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    // `initializeWithValue` (usehooks-ts default: true) reads
    // `window.matchMedia(...)` synchronously in the client's very first
    // render, before hydration — an unconditional SSR/CSR branch that
    // diverges from the server's `false` default on any viewport <=768px.
    // Force the SSR-safe default and let it settle post-mount instead.
    const isMobile = useMediaQuery('(max-width: 768px)', { initializeWithValue: false });
    const hasViewAsFlag = useFeatureFlag('studio-view-as');
    const hasSubsidiaryFlag = useFeatureFlag('studio-subsidiary');
    const showViewAsControl = shouldShowViewAsControl({
        isInternalMasquerade:
            session?.user?.isInternal === true && session?.user?.hasSubsidiaryAccess !== true,
        hasViewAsFlag,
        hasSubsidiaryFlag,
        subsidiaryOrgIds: session?.subsidiary?.orgIds,
    });

    const router = useRouter();

    const onToggle = useCallback(() => {
        setIsCollapsed((prev) => !prev);
    }, []);

    const handleProfileMenuAction = useCallback(
        (actionKey: Key) => {
            switch (actionKey) {
                case 'settings':
                    router.push(Routes.ACCOUNT);
                    break;
                case 'admin-dashboard':
                    router.push(Routes.USERS_REPORT);
                    break;
                case 'whats-new':
                    router.push(Routes.STUDIO_AI_RELEASE_NOTES);
                    break;
                case 'need-help':
                    setIsHelpModalOpen(true);
                    break;
                case 'sign-out':
                    router.push(Routes.SIGNOUT);
                    break;
            }
        },
        [router],
    );

    const handleSupportSubmit = useCallback(
        async ({ selectValue, messageText }: { selectValue: string; messageText: string }) => {
            try {
                await studioService.sendSupportTicket({
                    subject: `Studio Help - ${selectValue}`,
                    userInput: messageText,
                    userEmail: session?.user?.email,
                    userName: session?.user?.name,
                });
                setIsHelpModalOpen(false);
            } catch (error) {
                console.error('Failed to submit support request:', error);
            }
        },
        [setIsHelpModalOpen, session],
    );

    return (
        <SidebarDrawer
            className={cn('rounded-lg', {
                'min-w-[256px]': !isCollapsed,
                'min-w-[72px]': isCollapsed,
            })}
            hideCloseButton={true}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
        >
            <NavShell
                isCollapsed={isCollapsed}
                onToggle={isMobile ? onOpenChange : onToggle}
                onProfileMenuAction={handleProfileMenuAction}
                viewAsControl={
                    showViewAsControl ? <ViewAsSidebarControl isCollapsed={isCollapsed} /> : null
                }
            />
            <SubmitMessageModal
                onSubmit={handleSupportSubmit}
                isOpen={isHelpModalOpen}
                onOpenChange={setIsHelpModalOpen}
                header="Need Help"
                subheader={<p>Need help? Ask Support below.</p>}
                selectName="Issue Area"
                selectOptions={ISSUE_AREA_OPTIONS}
                messagePlaceholder="Describe your issue..."
            />
        </SidebarDrawer>
    );
}

WithSidebar.displayName = 'WithSidebar';
